// client/src/coms/views/canvas_parts/canvas_room_view_parts/shapeUtils.js
import { buildRgba } from './colorUtils';
import { clampOpacity } from './toolState';

export function drawShape(ctx, shape, options) {
  if (!ctx || !shape || !shape.start || !shape.end) return;

  const isEraser = options.isEraser;
  const strokeStyle = isEraser ? 'rgba(0,0,0,1)' : buildRgba(options.color, 1);

  ctx.save();
  ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
  ctx.globalAlpha = clampOpacity(options.opacity);
  ctx.strokeStyle = strokeStyle;
  ctx.lineWidth = options.size;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const x1 = shape.start.x;
  const y1 = shape.start.y;
  const x2 = shape.end.x;
  const y2 = shape.end.y;
  const left = Math.min(x1, x2);
  const top = Math.min(y1, y2);
  const width = Math.abs(x2 - x1);
  const height = Math.abs(y2 - y1);

  if (shape.tool === 'line') {
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  } else if (shape.tool === 'rectangle') {
    ctx.strokeRect(left, top, width, height);
  } else if (shape.tool === 'ellipse') {
    ctx.beginPath();
    ctx.ellipse(left + width / 2, top + height / 2, Math.max(0.5, width / 2), Math.max(0.5, height / 2), 0, 0, Math.PI * 2);
    ctx.stroke();
  }

  ctx.restore();
}


export function buildShapePathPoints(shape) {
  if (!shape || !shape.start || !shape.end) return [];

  const x1 = Number(shape.start.x);
  const y1 = Number(shape.start.y);
  const x2 = Number(shape.end.x);
  const y2 = Number(shape.end.y);

  if (![x1, y1, x2, y2].every(Number.isFinite)) return [];

  const left = Math.min(x1, x2);
  const top = Math.min(y1, y2);
  const width = Math.abs(x2 - x1);
  const height = Math.abs(y2 - y1);

  if (shape.tool === 'line') {
    return [
      { x: x1, y: y1 },
      { x: x2, y: y2 },
    ];
  }

  if (shape.tool === 'rectangle') {
    return [
      { x: left, y: top },
      { x: left + width, y: top },
      { x: left + width, y: top + height },
      { x: left, y: top + height },
      { x: left, y: top },
    ];
  }

  if (shape.tool === 'ellipse') {
    const centerX = left + (width / 2);
    const centerY = top + (height / 2);
    const radiusX = Math.max(0.5, width / 2);
    const radiusY = Math.max(0.5, height / 2);
    const circumference = Math.PI * 2 * Math.sqrt(((radiusX * radiusX) + (radiusY * radiusY)) / 2);
    const steps = Math.max(24, Math.min(360, Math.ceil(circumference / 3)));
    const points = [];

    for (let i = 0; i <= steps; i += 1) {
      const angle = (i / steps) * Math.PI * 2;
      points.push({
        x: centerX + (Math.cos(angle) * radiusX),
        y: centerY + (Math.sin(angle) * radiusY),
      });
    }

    return points;
  }

  return [];
}

export function buildShapePathPointSegments(shape) {
  if (!shape || shape.tool !== 'rectangle' || !shape.start || !shape.end) {
    const points = buildShapePathPoints(shape);
    return points.length ? [points] : [];
  }

  const x1 = Number(shape.start.x);
  const y1 = Number(shape.start.y);
  const x2 = Number(shape.end.x);
  const y2 = Number(shape.end.y);

  if (![x1, y1, x2, y2].every(Number.isFinite)) return [];

  const left = Math.min(x1, x2);
  const top = Math.min(y1, y2);
  const right = Math.max(x1, x2);
  const bottom = Math.max(y1, y2);

  if (Math.abs(right - left) < 0.5 || Math.abs(bottom - top) < 0.5) {
    const points = buildShapePathPoints(shape);
    return points.length ? [points] : [];
  }

  return [
    [
      { x: left, y: top },
      { x: right, y: top },
    ],
    [
      { x: right, y: top },
      { x: right, y: bottom },
    ],
    [
      { x: right, y: bottom },
      { x: left, y: bottom },
    ],
    [
      { x: left, y: bottom },
      { x: left, y: top },
    ],
  ];
}

export function isShapeTool(tool) {
  return tool === 'line' || tool === 'rectangle' || tool === 'ellipse';
}

export function isMarqueeTool(tool) {
  return tool === 'marquee_rect' || tool === 'marquee_ellipse' || tool === 'marquee_polygon' || tool === 'marquee_polygon_edit';
}
